<template>
  <div>
    <EmptyMessage v-if="noSelectedTokenIds">Select a word to show related articles</EmptyMessage>
    <template v-else>
      <LoadingSpinner v-if="$apollo.loading" />
      <EmptyMessage v-if="articles?.length === 0">
        No articles found for the current selection
      </EmptyMessage>
      <ArticleDetail
        v-else-if="selectedArticleUri"
        :key="selectedArticleUri"
        :articleUri="selectedArticleUri"
        :hasMore="articles?.length > 1"
        @select-article-uri="uri => (selectedArticleUri = uri)"
      />
      <ArticleList
        v-else-if="articlesByCollection"
        :collections="articlesByCollection"
        @select-article-uri="uri => (selectedArticleUri = uri)"
      />
    </template>
  </div>
</template>
<script>
  import mixins from '@/mixins';
  import gql from 'graphql-tag';
  import { DEFAULT_GROUP_KEY } from '@/store/constants';
  import { compareByProperty } from '@/common/sortUtils';
  import LoadingSpinner from '@/symphony/widgets/components/LoadingSpinner.vue';
  import EmptyMessage from '@/symphony/widgets/components/EmptyMessage.vue';
  import ArticleDetail from '@/symphony/widgets/articles-widget/ArticleDetail.vue';
  import ArticleList from '@/symphony/widgets/articles-widget/ArticleList.vue';

  export default {
    name: 'ArticlesWidget',
    mixins: [mixins.InputDataMixin, mixins.LocalizationMixin],
    components: {
      LoadingSpinner,
      EmptyMessage,
      ArticleDetail,
      ArticleList,
    },
    props: {
      doric: {
        inputs: {
          label: 'Word-level Articles',
          selectedTokenIds: {
            groupKey: DEFAULT_GROUP_KEY,
            value: 'selectedTokenIds',
          },
        },
      },
    },
    data() {
      return {
        selectedArticleUri: null,
      };
    },
    watch: {
      selectedTokenIds() {
        this.selectedArticleUri = null;
      },
      localizedArticles(newValue) {
        this.autoSelectArticleUri(newValue);
      },
    },
    computed: {
      parsedSelectedTokenIds() {
        try {
          return JSON.parse(this.selectedTokenIds);
        } catch (e) {
          return [];
        }
      },
      noSelectedTokenIds() {
        return (
          !Array.isArray(this.parsedSelectedTokenIds) || this.parsedSelectedTokenIds.length === 0
        );
      },
      localizedArticles() {
        if (!this.articles) {
          return null;
        }
        return this.articles
          .map(article => {
            const { uri, title } = article;
            const fallbackTitle = this.getLocalizedOrFallback(article, 'title');
            const description = this.getLocalizedOrFallback(article, 'description');
            return {
              uri,
              title,
              fallbackTitle,
              description,
              collection: article.collection,
            };
          })
          .sort(compareByProperty('fallbackTitle'));
      },
      articlesByCollection() {
        const collections = this.localizedArticles
          ? this.localizedArticles.reduce((acc, article) => {
              const collection =
                this.getLocalizedOrFallback(article.collection, 'label') || 'Uncategorized';
              if (!acc[collection]) {
                acc[collection] = [];
              }
              acc[collection].push(article);
              return acc;
            }, {})
          : {};
        return Object.entries(collections).sort((a, b) => a[0].localeCompare(b[0]));
      },
    },
    methods: {
      autoSelectArticleUri(articles) {
        if (articles?.length === 1) {
          this.selectedArticleUri = articles[0].uri;
        }
      },
    },
    apollo: {
      articles: {
        query: gql`
          query WordTokens($filters: WordTokenFilter, $language: String = "eng") {
            wordTokens(filters: $filters) {
              id
              articles {
                id
                uri
                title(language: $language, allowFallback: false)
                description(language: $language)
                localizations {
                  language
                  title
                  description
                }
                collection {
                  id
                  uri
                  label
                }
              }
            }
          }
        `,
        variables() {
          return {
            filters: {
              id: {
                inList: this.parsedSelectedTokenIds,
              },
            },
            language: this.lang,
          };
        },
        skip() {
          return this.noSelectedTokenIds;
        },
        update(data) {
          const tokenArticles = data.wordTokens.flatMap(token => token.articles);
          const uniqueArticles = tokenArticles.reduce((acc, article) => {
            if (!acc.some(a => a.uri === article.uri)) {
              acc.push(article);
            }
            return acc;
          }, []);
          return uniqueArticles;
        },
      },
    },
  };
</script>
